import { supabase } from '@/integrations/supabase/client';
import { useTelegramWebApp } from './useTelegramWebApp';
import { checkAndFixBroCode } from '@/utils/broCodeCheck';

export interface ApiOptions {
  useWalletAuth?: boolean;
  walletAddress?: string;
  useInternalApiKey?: boolean;
}

export const useAuthenticatedApi = () => {
  const { webApp, isTelegram } = useTelegramWebApp();

  const getAuthHeaders = (options?: ApiOptions): Record<string, string> => {
    const headers: Record<string, string> = {};
    
    // Use internal API key for system operations
    if (options?.useInternalApiKey) {
      headers['x-api-key'] = 'internal-generation-request';
      console.log('📤 [AUTH] Added internal API key to request headers');
      return headers;
    }
    
    // PRIMARY: Add BroCode if available (most reliable authentication)
    const brocode = checkAndFixBroCode();
    console.log('🔍 [AUTH DETAILED] BroCode after check/fix:', brocode);
    
    if (brocode) {
      headers['x-brocode'] = brocode;
      console.log(`📤 [AUTH] Added BroCode to request headers: ${brocode}`);
    } else {
      console.error('❌ [AUTH] CRITICAL: No BroCode found even after check/fix!');
      console.error('📋 [AUTH] localStorage ALL CONTENTS:', Object.fromEntries(Object.entries(localStorage)));
    }
    
    // FALLBACK: Add wallet address if available from localStorage or options
    // Check multiple storage keys for wallet address (unified system)
    const walletAddress = options?.walletAddress || 
      localStorage.getItem('connected_wallet_address') || // Standardized key
      localStorage.getItem('bromentum_wallet_address') || // useWalletPersistence key
      localStorage.getItem('walletAddress'); // Legacy key
      
    console.log('📋 [AUTH] Available auth data in localStorage:', {
      brocode: !!brocode,
      connected_wallet_address: !!localStorage.getItem('connected_wallet_address'),
      bromentum_wallet_address: !!localStorage.getItem('bromentum_wallet_address'),
      walletAddress: !!localStorage.getItem('walletAddress')
    });
    
    if (walletAddress) {
      headers['x-wallet-address'] = walletAddress;
      console.log(`📤 [AUTH] Added wallet address to request headers: ${walletAddress.slice(0, 8)}...`);
    } else if (!brocode) {
      console.warn('⚠️ [AUTH] No wallet address found in localStorage and no BroCode');
    }
    
    // FALLBACK: Add Telegram WebApp data if available
    if (isTelegram && webApp?.initData) {
      headers['x-telegram-data'] = webApp.initData;
      console.log('📤 [AUTH] Added Telegram auth data to request headers');
    }
    
    if (!brocode && !walletAddress && (!isTelegram || !webApp?.initData)) {
      console.error('❌ [AUTH] No authentication data available! User may not be initiated.');
    }
    
    return headers;
  };

  const invokeFunction = async (
    functionName: string, 
    body: any, 
    options?: ApiOptions
  ) => {
    const authHeaders = getAuthHeaders(options);
    
    console.log(`🔍 [AUTH DEBUG] Invoking function ${functionName}`);
    console.log(`🔍 [AUTH DEBUG] Auth headers:`, authHeaders);
    console.log(`🔍 [AUTH DEBUG] Body size:`, JSON.stringify(body).length, 'characters');
    console.log(`🔍 [AUTH DEBUG] Body content:`, body);
    
    // Add timeout to prevent infinite hanging
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      console.error(`❌ [AUTH TIMEOUT] Function ${functionName} timed out after 30 seconds`);
      controller.abort();
    }, 30000);
    
    try {
      const { data, error } = await supabase.functions.invoke(functionName, {
        body,
        headers: authHeaders
      });
      
      clearTimeout(timeoutId);
      
      if (error) {
        console.error(`❌ [AUTH ERROR] Function ${functionName} failed:`, error);
        console.error(`❌ [AUTH ERROR] Full error object:`, {
          message: error.message,
          context: error.context,
          details: error.details,
          status: error.status,
          statusText: error.statusText
        });
        console.error(`❌ [AUTH ERROR] Headers sent:`, authHeaders);
        console.error(`❌ [AUTH ERROR] Body sent:`, body);
        
        // Extract actual error message from response if available
        let errorMessage = error.message || 'Unknown error';
        if (error.context?.response) {
          try {
            const responseText = await error.context.response.text();
            console.error(`❌ [AUTH ERROR] Response body:`, responseText);
            errorMessage = `${functionName} failed: ${responseText}`;
          } catch (e) {
            console.error(`❌ [AUTH ERROR] Could not read response body:`, e);
          }
        }
        
        throw new Error(errorMessage);
      }
      
      console.log(`✅ [AUTH SUCCESS] Function ${functionName} succeeded`);
      return data;
    } catch (error: any) {
      clearTimeout(timeoutId);
      
      if (error.name === 'AbortError') {
        console.error(`❌ [AUTH TIMEOUT] Function ${functionName} was aborted due to timeout`);
        throw new Error(`Request to ${functionName} timed out after 30 seconds`);
      }
      
      // Re-throw existing errors
      throw error;
    }
  };

  return {
    invokeFunction,
    getAuthHeaders,
    isAuthenticated: isTelegram && !!webApp?.initData
  };
};